import { ButtonDisplaySize, ButtonDisplayStyle, DisplayStyle, ExtraButtonDisplayStyle, HoverCursorTypes, IconTypes, TextDisplayStyle } from '../Enums/UiEnums';
import { assertInternally } from './Environment';

// Get the CSS class for an alert using the didplay style
//  Include 'alert' if requested
export function bootstrapAlertClass(style: DisplayStyle | string, includeAlert: boolean = false): string| undefined {
    style = toDisplayStyle(style) ?? style;

    const prefix = includeAlert ? "alert " : "";
    switch(style)
    {
        case DisplayStyle.Primary: return prefix + "alert-primary";
        case DisplayStyle.Secondary: return prefix + "alert-secondary";
        case DisplayStyle.Success: return prefix + "alert-success";
        case DisplayStyle.Warning: return prefix + "alert-warning";
        case DisplayStyle.Danger: return prefix + "alert-danger";
        case DisplayStyle.Info: return prefix + "alert-info";
        case DisplayStyle.Light: return prefix + "alert-light";
        case DisplayStyle.Dark: return prefix + "alert-dark";
    }
}

// Get the CSS class for text using the didplay style
export function bootstrapTextClass(style: DisplayStyle | TextDisplayStyle | string | undefined): string| undefined {
    if (style)
        style = toDisplayStyle(style) ?? style;

    switch(style)
    {
        case DisplayStyle.Primary: return "text-primary";
        case DisplayStyle.Secondary: return "text-secondary";
        case DisplayStyle.Success: return "text-success";
        case DisplayStyle.Warning: return "text-warning";
        case DisplayStyle.Danger: return "text-danger";
        case DisplayStyle.Info: return "text-info";
        case DisplayStyle.Light: return "text-light";
        case DisplayStyle.Dark: return "text-dark";
        case TextDisplayStyle.Muted: return "text-muted";
    }
}

// Get the CSS class for an icon that matches the display style
export function themeIconsClass(style: DisplayStyle | string): string | undefined {
    style = toDisplayStyle(style) ?? style;

    switch(style){
        case DisplayStyle.Danger:
            return "fa-solid fa-skull-crossbones";

        case DisplayStyle.Warning:
            return "fa-solid fa-triangle-exclamation";

        case DisplayStyle.Primary:
        default:
            return "fa-solid fa-comment";
    }
}

// Convert a string to a display style
function toDisplayStyle(displayStyle: DisplayStyle | string) : DisplayStyle | undefined
{
    if ((<any>Object).values(DisplayStyle).includes(displayStyle))
        return <DisplayStyle>displayStyle;

    if (displayStyle)
        displayStyle = displayStyle.toLowerCase();

    switch(displayStyle) {
        case "primary": return DisplayStyle.Primary;
        case "secondary": return DisplayStyle.Secondary;
        case "success": return DisplayStyle.Success;
        case "warning": return DisplayStyle.Warning;
        case "danger": return DisplayStyle.Danger;
        case "info": return DisplayStyle.Info;
        case "light": return DisplayStyle.Light;
        case "dark": return DisplayStyle.Dark;
    }
}

export function bootstrapButtonClass(style: DisplayStyle | ButtonDisplayStyle | ExtraButtonDisplayStyle | string = DisplayStyle.Primary, size: ButtonDisplaySize = ButtonDisplaySize.Default, includeBtn: boolean = true): string | undefined {
    style = toDisplayStyle(style) ?? style;

    const classItems = [];

    if (includeBtn)
        classItems.push("btn");

    // Add button size?
    if (size)
    {
        switch(size)
        {
            case ButtonDisplaySize.XS:
                classItems.push("btn-xs");  // You have to include choice.css to have access to this
                break;

            case ButtonDisplaySize.Smaller:
                classItems.push("btn-smaller"); // You have to include choice.css to have access to this
                break;

            case ButtonDisplaySize.Small:
                classItems.push("btn-sm");
                break;
            case ButtonDisplaySize.Large:
                classItems.push("btn-lg");
                break;
    
            case ButtonDisplaySize.Default:
                break;
    
            default:
                alert("Unexpected button size");
                break;
        }
    }

    const buttonStyleMap: Record<DisplayStyle | ButtonDisplayStyle | string, string> = 
    {
        [DisplayStyle.Primary]: "btn-primary",
        [DisplayStyle.Secondary]: "btn-secondary",
        [DisplayStyle.Success]: "btn-success",
        [DisplayStyle.Warning]: "btn-warning",
        [DisplayStyle.Danger]: "btn-danger",
        [DisplayStyle.Info]: "btn-info",
        [DisplayStyle.Light]: "btn-light",
        [DisplayStyle.Dark]: "btn-dark",

        [ButtonDisplayStyle.Link]: "btn-link",

        [ButtonDisplayStyle.OutlinePrimary]: "btn-outline-primary",
        [ButtonDisplayStyle.OutlineSecondary]: "btn-outline-secondary",
        [ButtonDisplayStyle.OutlineSuccess]: "btn-outline-success",
        [ButtonDisplayStyle.OutlineWarning]: "btn-outline-warning",
        [ButtonDisplayStyle.OutlineDanger]: "btn-outline-danger",
        [ButtonDisplayStyle.OutlineInfo]: "btn-outline-info",
        [ButtonDisplayStyle.OutlineLight]: "btn-outline-light",
        [ButtonDisplayStyle.OutlineDark]: "btn-outline-dark",

        [ButtonDisplayStyle.TextPrimary]: "btn-text-primary",
        [ButtonDisplayStyle.TextSuccess]: "btn-text-success",
        [ButtonDisplayStyle.TextWarning]: "btn-text-warning",
        [ButtonDisplayStyle.TextDanger]: "btn-text-danger",

        [ExtraButtonDisplayStyle.OutlineGrey]: "btn-outline-dark border-grey"
    }

    // Add button style
    classItems.push(buttonStyleMap[style] ?? "btn-primary");

    return classItems.join(" ");
}

export function bootstrapBadgeClass(style: DisplayStyle | string, textDisplayStyle: DisplayStyle|TextDisplayStyle|undefined = undefined, includeBadge: boolean = true): string| undefined {
    style = toDisplayStyle(style) ?? style;

    const prefix = includeBadge ? "badge " : undefined;
    const backgroundStyle = bootstrapBackgroundColor(style, !textDisplayStyle);
    const textStyle = bootstrapTextClass(textDisplayStyle);

    return [prefix ,backgroundStyle, textStyle].join(" ");
}

export function bootstrapBackgroundColor(style: DisplayStyle | string, includeDefaultTextColor: boolean = true): string| undefined {
    const configInfo: Record<string, {bgClass: string, textColor: DisplayStyle}> = {
        [DisplayStyle.Primary]: {bgClass: "bg-primary", textColor: DisplayStyle.Light},
        [DisplayStyle.Secondary]: {bgClass: "bg-secondary", textColor: DisplayStyle.Light},
        [DisplayStyle.Success]: {bgClass: "bg-success", textColor: DisplayStyle.Light},
        [DisplayStyle.Warning]: {bgClass: "bg-warning", textColor: DisplayStyle.Dark},
        [DisplayStyle.Danger]: {bgClass: "bg-danger", textColor: DisplayStyle.Light},
        [DisplayStyle.Info]: {bgClass: "bg-info", textColor: DisplayStyle.Light},
        [DisplayStyle.Light]: {bgClass: "bg-light", textColor: DisplayStyle.Dark},
        [DisplayStyle.Dark]: {bgClass: "bg-dark", textColor: DisplayStyle.Light}        
    }

    style = toDisplayStyle(style) ?? style;
    const config = configInfo[style];
    return addTextClass(config.bgClass, includeDefaultTextColor ? config.textColor : undefined);
}

export function bootstrapSubtleBackgroundColor(style: DisplayStyle | string): string| undefined {
    const configInfo: Record<string, {bgClass: string, textColor: DisplayStyle}> = {
        [DisplayStyle.Primary]: {bgClass: "bg-primary-subtle", textColor: DisplayStyle.Light},
        [DisplayStyle.Secondary]: {bgClass: "bg-secondary-subtle", textColor: DisplayStyle.Light},
        [DisplayStyle.Success]: {bgClass: "bg-success-subtle", textColor: DisplayStyle.Light},
        [DisplayStyle.Warning]: {bgClass: "bg-warning-subtle", textColor: DisplayStyle.Dark},
        [DisplayStyle.Danger]: {bgClass: "bg-danger-subtle", textColor: DisplayStyle.Light},
        [DisplayStyle.Info]: {bgClass: "bg-info-subtle", textColor: DisplayStyle.Light},
        [DisplayStyle.Light]: {bgClass: "bg-light-subtle", textColor: DisplayStyle.Dark},
        [DisplayStyle.Dark]: {bgClass: "bg-dark-subtle", textColor: DisplayStyle.Light}        
    }

    style = toDisplayStyle(style) ?? style;
    const config = configInfo[style];
    return config.bgClass;
}


// Add a text class to a style string
export function addTextClass(currentClass: string, style: DisplayStyle | string | undefined): string | undefined
{
    if (!style)
        return currentClass;

    style = toDisplayStyle(style) ?? style;

    currentClass += " " + bootstrapTextClass(style);
    return currentClass.trim();
}

export function hoverCursorStyle(cursor: HoverCursorTypes|undefined): string|undefined {
    if (undefined === cursor)
        return;
    
    const cursorDict: Record<HoverCursorTypes, string> ={
        [HoverCursorTypes.Auto]: "cursor: auto",
        [HoverCursorTypes.ContextMenu]: "cursor: context-menu",
        [HoverCursorTypes.Default]: "cursor: default",
        [HoverCursorTypes.Help]: "cursor: help",
        [HoverCursorTypes.None]: "cursor: none",
        [HoverCursorTypes.Pointer]: "cursor: pointer",
        [HoverCursorTypes.Progress]: "cursor: progress",
        [HoverCursorTypes.Wait]: "cursor: wait",
    };

    return cursorDict[cursor];
}

export function iconClass(icon: IconTypes|undefined): string|undefined {
    if (undefined === icon)
        return;
 
    const iconCssMap: Record<IconTypes, string> = 
    {
        [IconTypes.Save]: "fa-solid fa-check",
        [IconTypes.Post]: "fa-solid fa-check",
        [IconTypes.Complete]: "fa-solid fa-check",
        [IconTypes.Add]: "fa-solid fa-plus",
        [IconTypes.Delete]: "fa-solid fa-trash",
        [IconTypes.Remove]: "fa-solid fa-eraser",
        [IconTypes.Export]: "fa-solid fa-download",
        [IconTypes.Import]: "fa-solid fa-upload",
        [IconTypes.Close]: "fa-solid fa-xmark",
        [IconTypes.Report]: "fa-solid fa-arrow-up-right-from-square",
        [IconTypes.Edit]: "fa-solid fa-pen",
        [IconTypes.Ignore]: "fa-solid fa-triangle-exclamation",
        [IconTypes.Warning]: "fa-solid fa-triangle-exclamation", 
        [IconTypes.Reverse]: "fa-solid fa-clock-rotate-left",
        [IconTypes.Hold]: "fa-solid fa-pause",
        [IconTypes.Calendar]: "fa-regular fa-calendar-days",
        [IconTypes.Communication]: "fa-solid fa-envelope",
        [IconTypes.Receipt]: "fa-solid fa-receipt",
        [IconTypes.Invoice]: "fa-solid fa-file-invoice",
        [IconTypes.InvoiceDollar]: "fa-solid fa-file-invoice-dollar",
        [IconTypes.Statement]: "fa-solid fa-file-lines",
        [IconTypes.Send]: "fa-regular fa-paper-plane",
        [IconTypes.Excel]: "fa-solid fa-file-excel",
        [IconTypes.Print]: "fa-solid fa-print",
        [IconTypes.Setting]: "fa-solid fa-gear",
        [IconTypes.Customize]: "fa-solid fa-ellipsis-vertical",
        [IconTypes.Tools]: "fa-solid fa-screwdriver-wrench",
        [IconTypes.Search]: "fa-solid fa-magnifying-glass",
        [IconTypes.Location]: "fa-solid fa-location-dot",
        [IconTypes.Next]: "fa-solid fa-forward",
        [IconTypes.Forward]: "fa-solid fa-forward-step",
        [IconTypes.Last]: "fa-solid fa-forward-fast",
        [IconTypes.Previous]: "fa-solid fa-backward",
        [IconTypes.Backward]: "fa-solid fa-backward-step",
        [IconTypes.First]: "fa-solid fa-backward-fast",
        [IconTypes.Lock]: "fa-solid fa-lock",
        [IconTypes.Unlock]: "fa-solid fa-unlock",
        [IconTypes.Reassign]: "fa-solid fa-people-arrows",
        [IconTypes.Attach]: "fa-solid fa-paperclip",
        [IconTypes.New]: "fa-regular fa-file",
        [IconTypes.Blank]: "fa-solid fa-gear",
        [IconTypes.Notification]: "fa-solid fa-bell",
        [IconTypes.User]: "fa-solid fa-user",
        [IconTypes.Users]: "fa-solid fa-user-group",
        [IconTypes.Filter]: "fa-solid fa-filter",
        [IconTypes.Comment]: "fa-solid fa-comment",
        [IconTypes.Comments]: "fa-solid fa-comments",
        [IconTypes.TurnRight]: "fa-solid fa-arrow-rotate-right",
        [IconTypes.TurnLeft]: "fa-solid fa-arrow-rotate-left",
        [IconTypes.Star]: "fa-solid fa-star",
        [IconTypes.Action]: "fa-solid fa-bolt",
        [IconTypes.Tag]: "fa-solid fa-tag",
        [IconTypes.Phone]: "fa-solid fa-phone",
        [IconTypes.Email]: "fa-solid fa-envelope", 
        [IconTypes.ChveronDown]: "fa-solid fa-chevron-down",
        [IconTypes.ChveronUp]: "fa-solid fa-chevron-up",
        [IconTypes.ChveronLeft]: "fa-solid fa-chevron-left",
        [IconTypes.ChveronRight]: "fa-solid fa-chevron-right",
        [IconTypes.AngleDoubleLeft]: "fas fa-angle-double-left",
        [IconTypes.AngleDoubleRight]: "fas fa-angle-double-right",
        [IconTypes.Ellipsis]: "fa-solid fa-ellipsis",
        [IconTypes.Doctor]: "fa-solid fa-user-doctor",
        [IconTypes.MedicalHouse]: "fa-solid fa-house-medical",
        [IconTypes.Trashcan]: "fa-solid fa-trash-can",
        [IconTypes.Unknown]: "fa-solid fa-circle-question", 
        [IconTypes.Ready]: "fa-solid fa-circle-check",
        [IconTypes.Refresh]: "fa-solid fa-rotate",
        [IconTypes.RxBase]: "fa-solid fa-circle-info",
        [IconTypes.RxLevel1]: "fa-solid fa-triangle-exclamation",
        [IconTypes.RxLevel2]: "fa-solid fa-triangle-exclamation",
        [IconTypes.RxLevel3]: "fa-solid fa-triangle-exclamation",
        [IconTypes.RxLevel9]: "fa-sharp fa-solid fa-circle-exclamation",
        [IconTypes.RxLevel10]: "fa-solid fa-circle-question",
        [IconTypes.InsuranceCard]: "fa-solid fa-id-card-clip",
        [IconTypes.PrimaryInsurance]: "fa-solid fa-p",
        [IconTypes.SecondaryInsurance]: "fa-solid fa-s",
        [IconTypes.TertiaryInsurance]: "fa-solid fa-t",
        [IconTypes.Error]: "fa-solid fa-circle-exclamation",
        [IconTypes.Clock]: "fa-solid fa-clock",
        [IconTypes.VerticalEllipsis]: "fa-solid fa-ellipsis-vertical",
        [IconTypes.SortNumeric]: "fa-solid fa-arrow-up-1-9",
        [IconTypes.CheckMark]: "fa-solid fa-check",
        [IconTypes.XMark]: "fa-solid fa-xmark",
        [IconTypes.PopOpen]: "fa-solid fa-arrow-up-right-from-square",
        [IconTypes.Clipboard]: "fa-solid fa-clipboard",
        [IconTypes.CreditCard]: "fa-regular fa-credit-card",
        [IconTypes.DeferPayment]: "fa-solid fa-money-bill-transfer",
        [IconTypes.Medications]: "fa-solid fa-pills",
        [IconTypes.Umbrella]: "fa-solid fa-umbrella",
        [IconTypes.Shield]: "fa-solid fa-shield-halved",
        [IconTypes.Inbox]: "fa-solid fa-inbox",
        [IconTypes.Bill]: "fa-solid fa-money-bill",
        [IconTypes.Order]: "fa-solid fa-list-check",
        [IconTypes.CircleXMark]: "fa-solid fa-circle-xmark",
        [IconTypes.Information]: "fa-solid fa-info",    
        [IconTypes.MMLConnected]: "fa-solid fa-file-circle-check",    
        [IconTypes.MMLInvite]: "fa-solid fa-file-circle-plus",    
        [IconTypes.MMLOptOut]: "fa-solid fa-file-circle-xmark",     
        [IconTypes.SpinnerDots]: "fa-solid fa-spinner",
        [IconTypes.CircleHollow]: "fa-regular fa-circle",
        [IconTypes.CircleExclamation]: "fa-solid fa-circle-exclamation",
        [IconTypes.CircleCheck]: "fa-solid fa-circle-check",
        [IconTypes.CircleInfo]: "fa-solid fa-circle-info",
        [IconTypes.CircleQuestion]: "fa-solid fa-circle-question",
        [IconTypes.View]: "fa-solid fa-eye",
        [IconTypes.Download]: "fa-solid fa-download",
        [IconTypes.DragDrop]: "fa-solid fa-grip-vertical",
        [IconTypes.Waiting]: "fa-solid fa-hourglass-start",
        [IconTypes.Processing]: "fa-solid fa-bolt-lightning",
        [IconTypes.Deleted]: "fa-regular fa-trash-can",
        [IconTypes.ArrowLeft]: "fa-solid fa-arrow-left",
        [IconTypes.ArrowRight]: "fa-solid fa-arrow-right",
        [IconTypes.ArrowUp]: "fa-solid fa-arrow-up",
        [IconTypes.ArrowDown]: "fa-solid fa-arrow-down",
        [IconTypes.Flask]: "fa-solid fa-flask",
        [IconTypes.Mobile]: "fa-solid fa-mobile",
        [IconTypes.MobileScreen]: "fa-solid fa-mobile-screen",
        [IconTypes.RegularClipboard]: "fa-regular fa-clipboard",
        [IconTypes.Sort]: "fa-solid fa-sort",
        [IconTypes.SortUp]: "fa-solid fa-sort-up",
        [IconTypes.SortDown]: "fa-solid fa-sort-down",
        [IconTypes.CsvFile]: "fa-solid fa-file-csv",
        [IconTypes.ToggleOn]: "fa-solid fa-toggle-on",
        [IconTypes.ToggleOff]: "fa-solid fa-toggle-off",
        [IconTypes.Message]: "fa-solid fa-message",
        [IconTypes.Briefcase]: "fa-solid fa-briefcase",
    };
    return iconCssMap[icon];
}

export type BootstrapPaddingValue = 0|1|2|3|4|5;

// If undefined is passed assume we want 'auto'
export function bootstrapPaddingClass(paddingValue?: BootstrapPaddingValue): string
{
    if (undefined === paddingValue)
        return "p-auto";  
    if (paddingValue >= 0 && paddingValue <= 5)
        return `p-${paddingValue}`;

    assertInternally(`Unexpected paddingValue: ${paddingValue}`);

    // Best guess if we've got a bad value
    return "p-auto";
}

function getBootstrapPaddingOrMarginClassByPrefix(prefix: string, paddingValue?: BootstrapPaddingValue): string|undefined
{
    if (undefined === paddingValue)
        return;  

    if (paddingValue >= 0 && paddingValue <= 5)
        return `${prefix}-${paddingValue}`;

    assertInternally(`Unexpected paddingValue: ${paddingValue}`);
    return;
}

export function bootstrapPaddingLeftClass(paddingValue?: BootstrapPaddingValue): string|undefined
{
    return getBootstrapPaddingOrMarginClassByPrefix("ps", paddingValue);
}

export function bootstrapPaddingRightClass(paddingValue?: BootstrapPaddingValue): string|undefined
{
    return getBootstrapPaddingOrMarginClassByPrefix("pe", paddingValue);
}

export function bootstrapPaddingTopClass(paddingValue?: BootstrapPaddingValue): string|undefined
{
    return getBootstrapPaddingOrMarginClassByPrefix("pt", paddingValue);
}

export function bootstrapPaddingBottomClass(paddingValue?: BootstrapPaddingValue): string|undefined
{
    return getBootstrapPaddingOrMarginClassByPrefix("pb", paddingValue);
}

export function bootstrapMarginLeftClass(paddingValue?: BootstrapPaddingValue): string|undefined
{
    return getBootstrapPaddingOrMarginClassByPrefix("ms", paddingValue);
}

export function bootstrapMarginRightClass(paddingValue?: BootstrapPaddingValue): string|undefined
{
    return getBootstrapPaddingOrMarginClassByPrefix("me", paddingValue);
}

export function bootstrapMarginTopClass(paddingValue?: BootstrapPaddingValue): string|undefined
{
    return getBootstrapPaddingOrMarginClassByPrefix("mt", paddingValue);
}

export function bootstrapMarginBottomClass(paddingValue?: BootstrapPaddingValue): string|undefined
{
    return getBootstrapPaddingOrMarginClassByPrefix("mb", paddingValue);
}
